import { buildPixPayload, detectPixKeyType, formatPixKeyLabel, normalizePixKey } from '@/lib/pix'
import type { RadioSettings } from '@/types/radio'
import {Copy, HeartHandshake} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useMemo, useState } from 'react'

function PixDonate({ settings }: { settings: RadioSettings | null }) {
  const [copied, setCopied] = useState(false)
  const pixKey = (settings?.pixKey || '').trim()
  const keyType = (settings?.pixKeyType as ReturnType<typeof detectPixKeyType>) || detectPixKeyType(pixKey)
  const payload = useMemo(
    () =>
      buildPixPayload({
        pixKey,
        pixKeyType: keyType,
        beneficiaryName: settings?.pixBeneficiaryName,
        beneficiaryCity: settings?.pixBeneficiaryCity || settings?.city,
        message: settings?.pixMessage || 'Doacao Bonita FM',
      }),
    [keyType, pixKey, settings?.city, settings?.pixBeneficiaryCity, settings?.pixBeneficiaryName, settings?.pixMessage],
  )

  if (!pixKey || !payload) {
    return (
      <article className="rounded-[28px] bg-[#1238c4] p-5">
        <div className="mb-3 flex items-center gap-2">
          <HeartHandshake />
          <h2 className="font-display text-xl font-black">DOE E AJUDE A MANTER NOSSA RÁDIO</h2>
        </div>
        <p className="text-sm text-white/80">Cadastre uma chave PIX válida no painel para gerar o QR Code de doação.</p>
      </article>
    )
  }

  const displayKey = normalizePixKey(pixKey, keyType)
  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(payload)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2200)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <article className="rounded-[28px] bg-[#1238c4] p-5">
      <div className="mb-3 flex items-center gap-2">
        <HeartHandshake />
        <h2 className="font-display text-xl font-black leading-tight">DOE E AJUDE A MANTER NOSSA RÁDIO</h2>
      </div>
      <p className="text-sm text-white/85">Escaneie o QR Code PIX sem valor definido. Você escolhe o valor no aplicativo do banco.</p>
      <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <div className="rounded-2xl bg-white p-3 shadow-lg">
          <QRCodeSVG value={payload} size={148} level="M" includeMargin={false} />
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-extrabold text-[#ffd21f]">Beneficiário</p>
          <p className="mt-1 font-bold">{settings?.pixBeneficiaryName || settings?.stationName || 'Bonita FM'}</p>
          {settings?.pixDocument ? <p className="text-white/80">Documento: {settings.pixDocument}</p> : null}
          <p className="text-white/80">{settings?.pixBeneficiaryCity || settings?.city || 'São José do Seridó/RN'}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-wide text-white/70">{formatPixKeyLabel(keyType)}</p>
          <p className="break-all font-semibold">{displayKey}</p>
          <button
            type="button"
            onClick={() => void copyPix()}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-black/30 px-3 py-2 text-xs font-bold hover:bg-black/45"
          >
            <Copy size={14} /> {copied ? 'PIX copiado' : 'Copiar código PIX'}
          </button>
        </div>
      </div>
    </article>
  )
}

export default PixDonate
